import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Tenant } from '@shared/types/Tenant';
import { saveTenantProfile } from '@application/persistence/TenantProfileService';
import { ToastService } from '@presentation/components/Toast';
import { EntrepriseIdentiteSchema } from '@shared/validation/EntrepriseSchema';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { mountCharteClientPanel } from '@presentation/views/parametres/CharteClientPanel';
import { LOGO_MAX_BYTES, LOGO_MAX_BYTES_SYNCED } from '@domain/branding/logoSanitizer';
import type { CompanyBranding } from '@domain/branding/CompanyBranding';
import {
  cfpParDefaut,
  contributionsEntreprise,
  fnhParDefaut,
  STATUTS_FISCAUX_EMPLOYEUR,
  statutFiscalParDefaut,
  type ContributionsEmployeurSettings,
  type StatutFiscalEmployeur,
} from '@domain/paie/ContributionsEmployeur';
import {
  CONVENTIONS,
  appliquerConvention,
  getConventionByKey,
  getConventionActive,
  resolveConventionKey,
  resumeConvention,
} from '@application/rh/ConventionsCollectives';

function setVal(section: HTMLElement, id: string, value: string | number | undefined): void {
  const el = section.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
  if (el && value !== undefined && value !== null) el.value = String(value);
}
function getVal(section: HTMLElement, id: string): string {
  return section.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value ?? '';
}

function estStatutFiscal(v: string): v is StatutFiscalEmployeur {
  return (STATUTS_FISCAUX_EMPLOYEUR as readonly string[]).includes(v);
}
function getCheck(section: HTMLElement, id: string): boolean {
  return section.querySelector<HTMLInputElement>(`#${id}`)?.checked ?? false;
}
function setCheck(section: HTMLElement, id: string, value: boolean): void {
  const el = section.querySelector<HTMLInputElement>(`#${id}`);
  if (el) el.checked = value;
}
function setTexte(section: HTMLElement, id: string, texte: string): void {
  const el = section.querySelector<HTMLElement>(`#${id}`);
  if (el) el.textContent = texte;
}

/** Statut sélectionné à l'écran (valeur invalide = déduit de la forme juridique sélectionnée). */
function statutSelectionne(section: HTMLElement): StatutFiscalEmployeur {
  const v = getVal(section, 'ent-statut-employeur');
  return estStatutFiscal(v) ? v : statutFiscalParDefaut(getVal(section, 'ent-forme-juridique'));
}

/** Textes « valeur par défaut » des deux interrupteurs, selon le statut sélectionné. */
function majTextesDefauts(section: HTMLElement): void {
  const statut = statutSelectionne(section);
  const forme = getVal(section, 'ent-forme-juridique');
  setTexte(
    section,
    'ent-fnh-defaut',
    `Valeur par défaut : ${fnhParDefaut(statut) ? 'oui' : 'non'}.`,
  );
  setTexte(
    section,
    'ent-cfp-defaut',
    `Valeur par défaut pour ce statut : ${cfpParDefaut(statut) ? 'oui' : 'non'}.`,
  );
  setTexte(
    section,
    'ent-statut-hint',
    statutFiscalParDefaut(forme) === 'autre' && statut === 'autre'
      ? 'Statut non déduit avec certitude de la forme juridique : précisez-le (société soumise à l’IS, BIC ou BNC) pour appliquer la CFP.'
      : '',
  );
}

/** Réglages FNH / CFP à enregistrer : seuls les choix qui diffèrent de la valeur par défaut sont mémorisés. */
export function contributionsEmployeurAEnregistrer(
  forme: string,
  statut: StatutFiscalEmployeur,
  fnh: boolean,
  cfp: boolean,
): ContributionsEmployeurSettings | undefined {
  const reglages: ContributionsEmployeurSettings = {};
  if (statut !== statutFiscalParDefaut(forme)) reglages.statut_fiscal = statut;
  if (fnh !== fnhParDefaut(statut)) reglages.fnh = fnh;
  if (cfp !== cfpParDefaut(statut)) reglages.cfp = cfp;
  return Object.keys(reglages).length > 0 ? reglages : undefined;
}

function fillConventionResume(section: HTMLElement, key: string): void {
  const conv = getConventionByKey(key);
  const resume = resumeConvention(key);
  setVal(section, 'ent-conv-prime', resume.prime_anciennete);
  setVal(section, 'ent-conv-plafond', resume.plafond_anciennete);
  setVal(section, 'ent-conv-conges', resume.conges);
  setVal(section, 'ent-conv-licenciement', resume.indemnite_licenciement);
  setVal(section, 'ent-conv-treizieme', resume.treizieme);
  const sources = section.querySelector('#ent-conv-sources');
  if (sources) {
    sources.textContent = conv.sources.join(' · ');
  }
}

function populateConventionSelect(section: HTMLElement, selectedKey: string): void {
  const sel = section.querySelector<HTMLSelectElement>('#ent-convention');
  if (!sel) return;
  sel.innerHTML = CONVENTIONS.map(
    (c) =>
      `<option value="${c.key}" ${c.key === selectedKey ? 'selected' : ''}>${c.label}</option>`,
  ).join('');
  fillConventionResume(section, selectedKey);
}

export const entrepriseController: ViewController = async (section, ctx, shell) => {
  const tenant = ctx.tenantContext.tryGetTenant();
  if (!tenant) return;
  const openedTenant: Tenant = tenant;
  const canMutate = canMutateRecords(shell.permission);

  setVal(section, 'ent-nom', tenant.raison_sociale ?? tenant.nom);
  setVal(section, 'ent-sigle', tenant.code);
  setVal(section, 'ent-nif', tenant.nif);
  setVal(section, 'ent-rccm', tenant.rccm);
  setVal(section, 'ent-cnss', tenant.parametres?.numero_cnss_employeur);
  setVal(section, 'ent-cnamgs', tenant.parametres?.numero_cnamgs_employeur);
  setVal(section, 'ent-adresse', tenant.adresse);
  setVal(section, 'ent-ville', tenant.ville);
  setVal(section, 'ent-tel', tenant.telephone);
  setVal(section, 'ent-email', tenant.email);
  setVal(section, 'ent-activite', tenant.secteur_activite);
  setVal(section, 'ent-periode', tenant.periode_courante);
  setVal(section, 'ent-forme-juridique', tenant.forme_juridique);
  setVal(section, 'ent-capital-social', tenant.capital_social);
  setVal(section, 'ent-date-creation', tenant.date_creation);
  setVal(section, 'ent-capital-libere', tenant.parametres?.capital_libere);
  setVal(section, 'ent-capital-appele', tenant.parametres?.capital_appele);
  setVal(section, 'ent-profil-fiscal', tenant.parametres?.profil_fiscal ?? tenant.regime_fiscal);
  setVal(section, 'ent-profil-sectoriel', tenant.parametres?.profil_sectoriel);
  setVal(section, 'ent-systeme-comptable', tenant.parametres?.systeme_comptable);
  setVal(section, 'ent-regime-forfait', tenant.parametres?.regime_forfaitaire);

  // Contributions patronales FNH / CFP : statut fiscal et interrupteurs de l'entreprise (valeurs par défaut comprises).
  const contributions = contributionsEntreprise(tenant.forme_juridique, tenant.parametres);
  setVal(section, 'ent-statut-employeur', contributions.statut);
  setCheck(section, 'ent-appliquer-fnh', contributions.fnh.actif);
  setCheck(section, 'ent-appliquer-cfp', contributions.cfp.actif);
  majTextesDefauts(section);
  const statutSelect = section.querySelector<HTMLSelectElement>('#ent-statut-employeur');
  if (statutSelect) {
    // Changer de statut remet les deux interrupteurs à leur valeur par défaut pour ce statut (modifiables ensuite).
    statutSelect.onchange = () => {
      const statut = statutSelectionne(section);
      setCheck(section, 'ent-appliquer-fnh', fnhParDefaut(statut));
      setCheck(section, 'ent-appliquer-cfp', cfpParDefaut(statut));
      majTextesDefauts(section);
    };
  }
  const formeSelect = section.querySelector<HTMLSelectElement>('#ent-forme-juridique');
  if (formeSelect) {
    // Tant que l'entreprise n'a pas choisi de statut, la forme juridique le déduit.
    formeSelect.onchange = () => {
      if (!contributions.statutChoisi) {
        const statut = statutFiscalParDefaut(formeSelect.value);
        setVal(section, 'ent-statut-employeur', statut);
        setCheck(section, 'ent-appliquer-fnh', fnhParDefaut(statut));
        setCheck(section, 'ent-appliquer-cfp', cfpParDefaut(statut));
      }
      majTextesDefauts(section);
    };
  }
  if (!canMutate) {
    for (const id of ['ent-statut-employeur', 'ent-appliquer-fnh', 'ent-appliquer-cfp']) {
      const el = section.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
      if (el) el.disabled = true;
    }
  }

  const storedKey = await getConventionActive(tenant.id);
  const initialKey = resolveConventionKey(
    storedKey !== 'general' ? storedKey : tenant.parametres?.convention_collective,
  );
  populateConventionSelect(section, initialKey);

  const convSelect = section.querySelector<HTMLSelectElement>('#ent-convention');
  if (convSelect) {
    convSelect.onchange = () => {
      void (async () => {
        if (!canMutate) {
          ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
          populateConventionSelect(section, initialKey);
          return;
        }
        const key = convSelect.value;
        await appliquerConvention(tenant.id, key);
        const conv = getConventionByKey(key);
        fillConventionResume(section, key);
        const current = ctx.tenantContext.tryGetTenant();
        if (current) {
          const updated: Tenant = {
            ...current,
            parametres: {
              ...current.parametres,
              convention_collective: conv.label,
            },
          };
          await saveTenantProfile(ctx, updated);
        }
        ToastService.success(`Convention « ${conv.label} » appliquée`);
      })();
    };
  }

  const charteHost = section.querySelector<HTMLElement>('#charte-client-host');
  if (charteHost) {
    const hybride = (tenant.mode_deploiement ?? tenant.deployment_mode) === 'hybride';
    mountCharteClientPanel(charteHost, {
      getTenant: () => ctx.tenantContext.tryGetTenant() ?? openedTenant,
      canMutate,
      // Hybride : le logo voyage dans le profil synchronisé (fiche limitée à 256 Kio).
      maxLogoBytes: hybride ? LOGO_MAX_BYTES_SYNCED : LOGO_MAX_BYTES,
      save: (branding) => saveBranding(branding),
      confirmReset: () =>
        confirmAction({
          title: 'Rétablir la charte par défaut ?',
          message:
            'Le logo et la charte de votre entreprise seront supprimés. Vos documents reprendront la charte neutre Nexxus.',
          confirmLabel: 'Rétablir',
          danger: true,
        }),
      notify: {
        success: (m) => ToastService.success(m),
        error: (m) => ToastService.error(m),
      },
    });
  }

  async function saveBranding(branding: CompanyBranding | null): Promise<void> {
    if (!canMutate) throw new Error('Action réservée au gestionnaire RH et à l’administrateur');
    const current = ctx.tenantContext.tryGetTenant() ?? openedTenant;
    const { branding: _previous, ...rest } = current;
    void _previous;
    const updated: Tenant = branding ? { ...rest, branding } : rest;
    await saveTenantProfile(ctx, updated);
    await recordAuditEvent(ctx, {
      action: 'update',
      entity: 'entreprise',
      entity_id: updated.id,
      description: branding
        ? 'Charte graphique et logo mis à jour'
        : 'Charte graphique rétablie par défaut',
      // Jamais les octets du logo dans le journal.
      payload: branding
        ? {
            logo: branding.logo
              ? `${branding.logo.mime} ${String(branding.logo.bytes)} octets`
              : 'aucun',
            primary_color: branding.primary_color,
            secondary_color: branding.secondary_color,
            font: branding.font,
          }
        : { branding: 'supprimée' },
    });
  }

  const w = window as unknown as Record<string, unknown>;
  w['saveEntreprise'] = () => void save();

  async function save(): Promise<void> {
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const convKey = getVal(section, 'ent-convention') || 'general';
    const conv = getConventionByKey(convKey);
    const parsed = EntrepriseIdentiteSchema.safeParse({
      nom: getVal(section, 'ent-nom'),
      code: getVal(section, 'ent-sigle'),
      nif: getVal(section, 'ent-nif'),
      rccm: getVal(section, 'ent-rccm'),
      numero_cnss_employeur: getVal(section, 'ent-cnss'),
      numero_cnamgs_employeur: getVal(section, 'ent-cnamgs'),
      adresse: getVal(section, 'ent-adresse'),
      ville: getVal(section, 'ent-ville'),
      telephone: getVal(section, 'ent-tel'),
      email: getVal(section, 'ent-email'),
      secteur_activite: getVal(section, 'ent-activite'),
      periode_courante: getVal(section, 'ent-periode'),
      forme_juridique: getVal(section, 'ent-forme-juridique'),
      capital_social: getVal(section, 'ent-capital-social'),
      date_creation: getVal(section, 'ent-date-creation'),
      convention_collective: conv.label,
    });
    if (!parsed.success) {
      ToastService.error(parsed.error.issues[0]?.message ?? 'Saisie invalide');
      return;
    }
    const data = parsed.data;
    // Tenant COURANT (et non celui lu à l'ouverture) : l'onglet « Charte et logo » l'a peut-être mis à jour.
    const current = ctx.tenantContext.tryGetTenant() ?? openedTenant;
    await appliquerConvention(current.id, convKey);
    const updated: Tenant = {
      ...current,
      nom: data.nom || current.nom,
      raison_sociale: data.nom || current.raison_sociale,
      code: data.code || current.code,
      nif: data.nif || current.nif,
      rccm: data.rccm || current.rccm,
      adresse: data.adresse || current.adresse,
      ville: data.ville || current.ville,
      telephone: data.telephone || current.telephone,
      email: data.email || current.email,
      secteur_activite: data.secteur_activite || current.secteur_activite,
      periode_courante: data.periode_courante || current.periode_courante,
      forme_juridique: (data.forme_juridique ||
        current.forme_juridique) as Tenant['forme_juridique'],
      capital_social: data.capital_social ?? current.capital_social,
      date_creation: data.date_creation || current.date_creation,
      parametres: {
        ...current.parametres,
        numero_cnss_employeur: data.numero_cnss_employeur,
        numero_cnamgs_employeur: data.numero_cnamgs_employeur,
        convention_collective: conv.label,
        capital_libere: Number(getVal(section, 'ent-capital-libere')) || 0,
        capital_appele: Number(getVal(section, 'ent-capital-appele')) || 0,
        profil_fiscal: getVal(section, 'ent-profil-fiscal') || undefined,
        profil_sectoriel: getVal(section, 'ent-profil-sectoriel') || undefined,
        systeme_comptable: getVal(section, 'ent-systeme-comptable') || undefined,
        regime_forfaitaire: getVal(section, 'ent-regime-forfait') || undefined,
        contributions_employeur: contributionsEmployeurAEnregistrer(
          getVal(section, 'ent-forme-juridique') || current.forme_juridique,
          statutSelectionne(section),
          getCheck(section, 'ent-appliquer-fnh'),
          getCheck(section, 'ent-appliquer-cfp'),
        ),
      },
      regime_fiscal: (getVal(section, 'ent-profil-fiscal') ||
        current.regime_fiscal) as Tenant['regime_fiscal'],
    };
    await saveTenantProfile(ctx, updated);
    await recordAuditEvent(ctx, {
      action: 'update',
      entity: 'entreprise',
      entity_id: updated.id,
      description: 'Fiche entreprise mise à jour',
      payload: {
        contributions_employeur: updated.parametres.contributions_employeur ?? 'valeurs par défaut',
      },
    });
    ToastService.success('Paramètres entreprise enregistrés');
    void import('../OnboardingChecklist').then(({ markOnboardingStep }) => {
      markOnboardingStep('entreprise');
    });
  }
};
